import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Eye, Plus } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { GET_FEATURE_WIZARD, GET_FEATURE_WIZARDS, GET_FEATURE_WIZARD_STEP_REGISTRY } from '../../../graphql/queries';
import { SET_FEATURE_WIZARD_STATUS, UPSERT_FEATURE_WIZARD } from '../../../graphql/mutations';
import { blankDraft, blankStep, draftFromWizard, draftProblem, renumber, toStepConfig, toUpsertInput } from './draft';
import { StepCard } from './StepCard';
import { WizardFields } from './WizardFields';
import { WizardPreviewModal } from './PreviewModal';
import { WizardResults } from './Results';
import type { WizardDraft, WizardRecord, WizardRegistryEntry, WizardStatus } from './types';

type EditorTab = 'edit' | 'results';

const TAB_LABELS: Record<EditorTab, string> = { edit: 'Edit wizard', results: 'Results' };

function TabNav({ tab, onSelect }: { tab: EditorTab; onSelect: (next: EditorTab) => void }) {
  return (
    <div role="tablist" className="flex gap-4 border-b border-gray-200">
      {(Object.keys(TAB_LABELS) as EditorTab[]).map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={tab === key}
          onClick={() => onSelect(key)}
          className={`-mb-px border-b-2 px-1 pb-2 text-sm ${
            tab === key ? 'border-gray-900 font-medium text-gray-900' : 'border-transparent text-gray-500'
          }`}
        >
          {TAB_LABELS[key]}
        </button>
      ))}
    </div>
  );
}

/** One page serves both create and edit; a null id on the upsert is the only difference. */
function FeatureWizardEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [draft, setDraft] = useState<WizardDraft | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: registryData } = useQuery<{ featureWizardStepRegistry: WizardRegistryEntry[] }>(
    GET_FEATURE_WIZARD_STEP_REGISTRY
  );
  const { data: wizardData } = useQuery<{ featureWizard: WizardRecord | null }>(GET_FEATURE_WIZARD, {
    variables: { id },
    skip: !id,
  });

  const [upsert, { loading: saving }] = useMutation(UPSERT_FEATURE_WIZARD, {
    refetchQueries: [{ query: GET_FEATURE_WIZARDS }],
  });
  const [setStatus, { loading: settingStatus }] = useMutation(SET_FEATURE_WIZARD_STATUS, {
    refetchQueries: [{ query: GET_FEATURE_WIZARDS }],
  });

  const registry = registryData?.featureWizardStepRegistry ?? [];
  const stepType = registry[0];

  const registryByCode = useMemo(() => new Map(registry.map((entry) => [entry.code, entry])), [registry]);

  useEffect(() => {
    if (draft) return;
    if (!stepType) return;
    if (id) {
      if (!wizardData?.featureWizard) return;
      setDraft(draftFromWizard(wizardData.featureWizard));
      return;
    }
    const fresh = blankDraft(stepType);
    setDraft(fresh);
    setCollapsed({ [fresh.steps[0].key]: false });
  }, [draft, stepType, id, wizardData]);

  if (!draft) {
    return (
      <AdminLayout title="Feature wizard" description="Loading…">
        <div className="text-gray-500">Loading…</div>
      </AdminLayout>
    );
  }

  const isCreate = !draft.id;
  const isPublished = draft.status === 'PUBLISHED';
  const problem = draftProblem(draft);
  const busy = saving || settingStatus;
  const stepCount = draft.steps.length;

  // Results are a tab rather than a section: reading them runs aggregates, so they load only
  // when asked for. The param carries the choice so the list can link straight to results.
  const tab: EditorTab = params.get('tab') === 'results' && draft.id ? 'results' : 'edit';

  const selectTab = (next: EditorTab) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'edit') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  const patch = (changes: Partial<WizardDraft>) => setDraft({ ...draft, ...changes });

  const updateStep = (key: string, config: Record<string, unknown>) => {
    patch({ steps: draft.steps.map((step) => (step.key === key ? { ...step, config } : step)) });
  };

  const addStep = () => {
    if (!stepType) return;
    const next = blankStep(stepType, stepCount + 1);
    setCollapsed({ ...collapsed, [next.key]: false });
    patch({ steps: renumber([...draft.steps, next]) });
  };

  // A local list edit: the upsert replaces the step list wholesale, so dropping a step and
  // renumbering from 1 is the whole repair. The hand-off lives on the wizard, not the last step.
  const removeStep = (key: string) => {
    patch({ steps: renumber(draft.steps.filter((step) => step.key !== key)) });
  };

  const persist = async (): Promise<string | null> => {
    const result = await upsert({ variables: { input: toUpsertInput(draft) } });
    const saved = result.data?.upsertFeatureWizard as WizardRecord | undefined;
    if (!saved) return null;
    // The local step rows ARE what was just saved, so they are kept: re-keying them from the
    // response would collapse every card the author had open for no gain.
    setDraft((current) =>
      current
        ? { ...current, id: saved.id, slug: saved.slug, status: saved.status, publishedAt: saved.publishedAt }
        : current
    );
    return saved.id;
  };

  const handleSave = async () => {
    setError(problem);
    if (problem) return;
    try {
      const savedId = await persist();
      if (savedId && isCreate) navigate(`/admin/feature-wizards/${savedId}`);
    } catch (saveError) {
      setError((saveError as Error).message);
    }
  };

  const handleStatus = async (status: WizardStatus) => {
    if (status === 'PUBLISHED') {
      setError(problem);
      if (problem) return;
    }
    try {
      const savedId = status === 'PUBLISHED' ? await persist() : draft.id;
      if (!savedId) return;
      const result = await setStatus({ variables: { id: savedId, status } });
      const updated = result.data?.setFeatureWizardStatus;
      setDraft((current) =>
        current
          ? { ...current, id: savedId, status, publishedAt: updated?.publishedAt ?? current.publishedAt }
          : current
      );
      if (isCreate) navigate(`/admin/feature-wizards/${savedId}`);
    } catch (statusError) {
      setError((statusError as Error).message);
    }
  };

  return (
    <AdminLayout
      title={isCreate ? 'New wizard' : 'Edit wizard'}
      description={
        isCreate
          ? 'A draft. Nothing is live and no link works until you publish.'
          : 'Editing never changes this wizard’s parameter, so links a campaign already sent keep working.'
      }
    >
      <div className="space-y-6">
        <Link
          to="/admin/feature-wizards"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to feature wizards
        </Link>

        {draft.id && <TabNav tab={tab} onSelect={selectTab} />}

        {tab === 'results' && draft.id && <WizardResults wizardId={draft.id} />}

        {tab === 'edit' && (
          <>
            <WizardFields draft={draft} onChange={patch} />

            <div className="rounded-lg bg-white p-6 shadow">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Steps</h3>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {stepCount} {stepCount === 1 ? 'step' : 'steps'} · each extra step is two more recorded events per
                    person who opens the wizard, so length has a cost in the funnel.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 font-normal"
                  onClick={() => setPreviewIndex(0)}
                  aria-label="Preview wizard from step one"
                >
                  <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Preview wizard
                </Button>
              </div>

              {draft.steps.map((step, index) => (
                <StepCard
                  key={step.key}
                  step={step}
                  isLast={index === stepCount - 1}
                  isPublished={isPublished}
                  canRemove={stepCount > 1}
                  collapsed={collapsed[step.key] ?? true}
                  schema={registryByCode.get(step.stepCode)?.configSchema}
                  handoffHref={draft.handoffHref}
                  onToggle={() => setCollapsed({ ...collapsed, [step.key]: !(collapsed[step.key] ?? true) })}
                  onChange={(config) => updateStep(step.key, config)}
                  onRemove={() => removeStep(step.key)}
                  onPreview={() => setPreviewIndex(index)}
                />
              ))}

              <div className="mt-3">
                <Button type="button" variant="outline" size="sm" className="gap-1.5 font-normal" onClick={addStep}>
                  <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Add step
                </Button>
                <p className="mt-2 text-xs text-gray-500">
                  A wizard needs at least one step, so the remove control is off while this is the only one. There is no
                  upper limit.
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-white p-6 shadow">
              {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" onClick={handleSave} disabled={busy}>
                  {saving ? 'Saving…' : isCreate ? 'Save draft' : 'Save'}
                </Button>
                {!isPublished && (
                  <Button
                    type="button"
                    variant="default"
                    className="bg-gray-900 hover:bg-gray-800"
                    onClick={() => handleStatus('PUBLISHED')}
                    disabled={busy}
                  >
                    Publish
                  </Button>
                )}
                <span className="flex-1" />
                {draft.id && draft.status !== 'ARCHIVED' && (
                  <Button
                    type="button"
                    variant="outline"
                    className="font-normal text-red-700 hover:bg-red-50 hover:text-red-800"
                    onClick={() => handleStatus('ARCHIVED')}
                    disabled={busy}
                  >
                    Archive
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {previewIndex !== null && stepCount > 0 && (
        <WizardPreviewModal
          steps={draft.steps.map((step) => toStepConfig(step.config))}
          initialStepIndex={previewIndex}
          source="editor"
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </AdminLayout>
  );
}

export default FeatureWizardEditorPage;
